import { useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  StopOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  UserOutlined,
  MailOutlined,
  IdcardOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../services/api/apiClient";
import { usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";
import PhoneInput from "../../components/PhoneInput";

const { Text } = Typography;

interface AuditorRow {
  id: number;
  name: string;
  code: string | null;
  email: string | null;
  phone_number: string | null;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  audit_company: number | null;
  audit_company_name: string | null;
  portfolio: number | null;
  portfolio_display: string | null;
}

// Mirrors backend `apps.master_data.helpers.COUNTRIES`. Keep in sync if
// new portfolios are added there.
const PORTFOLIO_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "España" },
  { value: 2, label: "Perú" },
  { value: 3, label: "Chile" },
  { value: 4, label: "Sudáfrica" },
  { value: 5, label: "Turquía" },
  { value: 6, label: "Egipto" },
  { value: 7, label: "Uruguay" },
  { value: 8, label: "Portugal" },
  { value: 9, label: "Italia" },
];

interface FormValues {
  first_name: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  code?: string;
  portfolio?: number;
  is_active: boolean;
}

/**
 * Coordinator view: manage the auditors of the coordinator's audit
 * company. Scope is enforced by the backend (`user_type=5`,
 * `audit_company == coordinator.audit_company`).
 */
export const CoordinatorAuditors = () => {
  const { t } = useTranslation();
  const { message, modal } = AntdApp.useApp();
  // Tracking en curso del envío de credenciales por fila (para
  // mostrar loading en el botón concreto que se pulsó).
  const [sendingCredentialsId, setSendingCredentialsId] = useState<number | null>(null);
  const [rows, setRows] = useState<AuditorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AuditorRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<FormValues>();

  usePageTitle(t("coordinator.auditors.title", "Gestión de auditores"));

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(
        "/v1/coordinator/auditors/?page_size=500&ordering=name",
      );
      const list = res.data?.data ?? res.data?.results ?? res.data ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (err: any) {
      message.error(
        t("coordinator.auditors.load_error", "No se pudieron cargar los auditores"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.code || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.username || "").toLowerCase().includes(q),
    );
  }, [rows, searchText]);

  const handleOpenCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, portfolio: 1 });
    setModalOpen(true);
  };

  const handleOpenEdit = (row: AuditorRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      email: row.email || "",
      phone_number: row.phone_number || "",
      code: row.code || "",
      portfolio: row.portfolio ?? 1,
      is_active: row.is_active,
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const payload = {
        first_name: values.first_name?.trim() || "",
        last_name: values.last_name?.trim() || "",
        email: values.email?.trim() || "",
        phone_number: values.phone_number?.trim() || "",
        code: values.code?.trim() || "",
        portfolio: values.portfolio,
        is_active: values.is_active,
      };
      if (editing) {
        await apiClient.patch(
          `/v1/coordinator/auditors/${editing.id}/`,
          payload,
        );
        message.success(
          t("coordinator.auditors.update_success", "Auditor actualizado"),
        );
      } else {
        await apiClient.post("/v1/coordinator/auditors/", payload);
        message.success(
          t("coordinator.auditors.create_success", "Auditor creado"),
        );
      }
      handleClose();
      loadRows();
    } catch (err: any) {
      const data = err?.response?.data;
      const detail =
        (data && typeof data === "object"
          ? Object.values(data).flat().join(" · ")
          : null) || t("coordinator.auditors.save_error", "Error al guardar");
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (row: AuditorRow, nextActive: boolean) => {
    try {
      await apiClient.patch(`/v1/coordinator/auditors/${row.id}/`, {
        is_active: nextActive,
      });
      message.success(
        nextActive
          ? t("coordinator.auditors.activated", "Auditor activado")
          : t("coordinator.auditors.deactivated", "Auditor dado de baja"),
      );
      loadRows();
    } catch {
      message.error(t("coordinator.auditors.save_error", "Error al guardar"));
    }
  };

  // Envío de credenciales — el backend genera password aleatorio,
  // hace email al AppUser.email (fallback a User.email) y persiste
  // el nuevo password sólo si el envío es exitoso. Mismo flujo que
  // el admin ya usa en /admin/auditor-assignments/ pero scopeado
  // por audit_company del coordinator.
  const doSendCredentials = async (row: AuditorRow) => {
    setSendingCredentialsId(row.id);
    try {
      const resp = await apiClient.post(
        `/v1/coordinator/auditors/${row.id}/send-credentials/`,
        {},
      );
      const sentTo: string =
        (resp?.data as any)?.sent_to ?? row.email ?? "";
      message.success(
        t(
          "coordinator.auditors.send_credentials_success",
          "Credenciales enviadas a {{email}}",
          { email: sentTo },
        ),
      );
    } catch (e: any) {
      const resp =
        e?.originalError?.response?.data ??
        e?.response?.data ??
        e?.data;
      const detail =
        (typeof resp === "object" && resp?.detail) ||
        e?.message ||
        t(
          "coordinator.auditors.send_credentials_error",
          "No se pudieron enviar las credenciales",
        );
      message.error(String(detail));
    } finally {
      setSendingCredentialsId(null);
    }
  };

  const confirmSendCredentials = (row: AuditorRow) => {
    modal.confirm({
      title: t(
        "coordinator.auditors.send_credentials_title",
        "Enviar credenciales",
      ),
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p style={{ marginTop: 0 }}>
            {t(
              "coordinator.auditors.send_credentials_confirm",
              "Se generará una contraseña nueva y se enviará por email. La contraseña actual dejará de funcionar en cuanto el email se entregue con éxito.",
            )}
          </p>
          <p style={{ marginBottom: 0, fontSize: 13, color: "#8c8c8c" }}>
            {t(
              "coordinator.auditors.send_credentials_recipient",
              "Destinatario: {{name}} · {{email}}",
              { name: row.name || "", email: row.email || "" },
            )}
          </p>
        </div>
      ),
      okText: t("coordinator.auditors.send_credentials_ok", "Enviar"),
      okButtonProps: { icon: <MailOutlined /> },
      cancelText: t("common.cancel", "Cancelar"),
      centered: true,
      onOk: () => doSendCredentials(row),
    });
  };

  return (
    <Card>
      <PageHeader
        icon={<TeamOutlined />}
        title={t("coordinator.auditors.title", "Gestión de auditores")}
        subtitle={t(
          "coordinator.auditors.subtitle",
          "Crea, edita o da de baja a los auditores de tu empresa",
        )}
      />

      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t(
            "coordinator.auditors.search_placeholder",
            "Buscar por nombre, código o email...",
          )}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 400 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          {t("coordinator.auditors.create", "Nuevo auditor")}
        </Button>
      </Space>

      <Table<AuditorRow>
        dataSource={filtered}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      >
        <Table.Column<AuditorRow>
          title={t("auditor.name", "Nombre")}
          dataIndex="name"
          sorter={(a, b) => (a.name || "").localeCompare(b.name || "")}
          render={(value: string, record) => (
            <Space direction="vertical" size={0}>
              <Text>{value}</Text>
              {record.username && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.username}
                </Text>
              )}
            </Space>
          )}
        />
        <Table.Column<AuditorRow>
          title={t("auditor.code", "Código")}
          dataIndex="code"
          width={110}
          sorter={(a, b) => (a.code || "").localeCompare(b.code || "")}
        />
        <Table.Column<AuditorRow>
          title={t("auditor.email", "Email")}
          dataIndex="email"
          ellipsis
        />
        <Table.Column<AuditorRow>
          title={t("auditor.phone", "Teléfono")}
          dataIndex="phone_number"
          width={150}
        />
        <Table.Column<AuditorRow>
          title={t("auditor.portfolio", "Cartera")}
          dataIndex="portfolio_display"
          width={130}
          sorter={(a, b) =>
            (a.portfolio_display || "").localeCompare(b.portfolio_display || "")
          }
          filters={PORTFOLIO_OPTIONS.map((o) => ({
            text: o.label,
            value: o.value,
          }))}
          onFilter={(value, record) => record.portfolio === value}
          render={(value: string | null) =>
            value || <span style={{ color: "#bfbfbf" }}>—</span>
          }
        />
        <Table.Column<AuditorRow>
          title={t("auditor.status", "Estado")}
          dataIndex="is_active"
          width={120}
          align="center"
          filters={[
            { text: t("auditor.active", "Activo"), value: true },
            { text: t("auditor.inactive", "Inactivo"), value: false },
          ]}
          onFilter={(value, record) => record.is_active === value}
          render={(value: boolean) =>
            value ? (
              <Tag icon={<CheckCircleOutlined />} color="success">
                {t("auditor.active", "Activo")}
              </Tag>
            ) : (
              <Tag icon={<StopOutlined />} color="default">
                {t("auditor.inactive", "Inactivo")}
              </Tag>
            )
          }
        />
        <Table.Column<AuditorRow>
          title={t("common.actions", "Acciones")}
          key="actions"
          width={200}
          align="right"
          render={(_, record) => {
            const hasEmail = !!(record.email && record.email.trim());
            return (
              <Space size="small">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenEdit(record)}
                title={t("common.edit", "Editar")}
              />
              <Tooltip
                title={
                  hasEmail
                    ? t(
                        "coordinator.auditors.send_credentials",
                        "Enviar credenciales",
                      )
                    : t(
                        "coordinator.auditors.send_credentials_no_email",
                        "El auditor no tiene email. Añádelo con Editar antes de enviar credenciales.",
                      )
                }
              >
                <Button
                  size="small"
                  icon={<MailOutlined />}
                  disabled={!hasEmail}
                  loading={sendingCredentialsId === record.id}
                  onClick={() => confirmSendCredentials(record)}
                />
              </Tooltip>
              {record.is_active ? (
                <Popconfirm
                  title={t(
                    "coordinator.auditors.deactivate_confirm",
                    "¿Dar de baja al auditor?",
                  )}
                  okText={t("common.yes", "Sí")}
                  cancelText={t("common.no", "No")}
                  onConfirm={() => handleToggleActive(record, false)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    title={t("coordinator.auditors.deactivate", "Dar de baja")}
                  />
                </Popconfirm>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleToggleActive(record, true)}
                  title={t("coordinator.auditors.reactivate", "Reactivar")}
                />
              )}
              </Space>
            );
          }}
        />
      </Table>

      <Modal
        title={
          <Space>
            <UserOutlined />
            {editing
              ? t("coordinator.auditors.edit_title", "Editar auditor")
              : t("coordinator.auditors.create_title", "Nuevo auditor")}
          </Space>
        }
        open={modalOpen}
        onCancel={handleClose}
        onOk={() => form.submit()}
        okButtonProps={{ loading: submitting }}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnClose
        width={640}
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          requiredMark="optional"
        >
          <Divider orientation="left" plain style={{ marginTop: 0 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("coordinator.auditors.section_personal", "Datos personales")}
            </Text>
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="first_name"
                label={t("auditor.first_name", "Nombre")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "auditor.first_name_required",
                      "El nombre es obligatorio",
                    ),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                  maxLength={150}
                  placeholder={t("auditor.first_name_placeholder", "Ej.: Mónica")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="last_name"
                label={t("auditor.last_name", "Apellidos")}
              >
                <Input
                  maxLength={150}
                  placeholder={t("auditor.last_name_placeholder", "Ej.: Ortega Serna")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={14}>
              <Form.Item
                name="email"
                label={t("auditor.email", "Email")}
                rules={[
                  {
                    type: "email",
                    message: t("common.invalid_email", "Email no válido"),
                  },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                  maxLength={50}
                  placeholder="auditor@empresa.com"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item
                name="phone_number"
                label={t("auditor.phone", "Teléfono")}
              >
                <PhoneInput
                  placeholder={t(
                    "auditor.phone_placeholder",
                    "600 000 000",
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("coordinator.auditors.section_account", "Cuenta y cartera")}
            </Text>
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item
                name="code"
                label={t("auditor.code", "Código")}
                tooltip={t(
                  "coordinator.auditors.code_tooltip",
                  "Déjalo en blanco para auto-generar uno desde el nombre",
                )}
              >
                <Input
                  prefix={<IdcardOutlined style={{ color: "#bfbfbf" }} />}
                  maxLength={10}
                  placeholder={t("auditor.code_placeholder", "Auto")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={14}>
              <Form.Item
                name="portfolio"
                label={t("auditor.portfolio", "Cartera (país)")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "auditor.portfolio_required",
                      "Selecciona la cartera",
                    ),
                  },
                ]}
              >
                <Select
                  options={PORTFOLIO_OPTIONS}
                  placeholder={t(
                    "auditor.portfolio_placeholder",
                    "Selecciona el país",
                  )}
                  suffixIcon={<GlobalOutlined />}
                  getPopupContainer={(trigger) =>
                    trigger.parentElement || document.body
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="is_active"
            label={t("auditor.status", "Estado")}
            valuePropName="checked"
            tooltip={t(
              "coordinator.auditors.is_active_tooltip",
              "Desactiva al auditor para que no pueda iniciar sesión ni recibir auditorías.",
            )}
            style={{ marginBottom: 0 }}
          >
            <Switch
              checkedChildren={t("auditor.active", "Activo")}
              unCheckedChildren={t("auditor.inactive", "Inactivo")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CoordinatorAuditors;
